/**
 * Rugit — slider de rango sobre valores discretos.
 *
 * Los <input type="range"> son solo la UI. La verdad son los checkboxes
 * ocultos, que llevan el name/value real del filtro de Shopify. Al soltar
 * un handle marcamos los que caen dentro del rango y disparamos el mismo
 * evento que dispararía un click, para que el JS de filtrado del tema
 * (facets.js / collection-filters-form.js) lo procese sin modificaciones.
 */
class RugitRangeFilter extends HTMLElement {
  connectedCallback() {
    this.values = (this.dataset.values || '').split(',').filter(Boolean);
    if (this.values.length < 2) return;

    this.checkboxes = Array.from(this.querySelectorAll('input[type="checkbox"]'));
    this.handles = Array.from(this.querySelectorAll('input[type="range"]'));
    this.track = this.querySelector('.rugit-range__track');
    this.readoutMin = this.querySelector('[data-readout-min]');
    this.readoutMax = this.querySelector('[data-readout-max]');

    if (this.handles.length !== 2 || !this.track) return;

    this.handles.forEach((handle) => {
      handle.addEventListener('input', this.paint.bind(this));
      handle.addEventListener('change', this.commit.bind(this));
    });

    this.paint();
  }

  /** Los handles pueden cruzarse: siempre devolvemos [menor, mayor]. */
  get bounds() {
    const a = parseInt(this.handles[0].value, 10);
    const b = parseInt(this.handles[1].value, 10);
    return [Math.min(a, b), Math.max(a, b)];
  }

  /** Feedback visual durante el drag. No toca la URL ni pide resultados. */
  paint() {
    const [lo, hi] = this.bounds;
    const last = this.values.length - 1;

    this.track.style.setProperty('--range-min', (lo / last) * 100 + '%');
    this.track.style.setProperty('--range-max', (hi / last) * 100 + '%');

    if (this.readoutMin) this.readoutMin.textContent = this.values[lo];
    if (this.readoutMax) this.readoutMax.textContent = this.values[hi];

    if (typeof rugitSyncSummaries === 'function') rugitSyncSummaries();
  }

  /** Al soltar el handle: sincronizamos checkboxes y pedimos resultados. */
  commit() {
    const [lo, hi] = this.bounds;
    const isFullRange = lo === 0 && hi === this.values.length - 1;

    this.checkboxes.forEach((checkbox, i) => {
      // Rango completo = sin filtro. Deja la URL limpia y evita mandar
      // ocho params que no filtran nada.
      checkbox.checked = !isFullRange && i >= lo && i <= hi;
    });

    // Disparamos desde un checkbox real (no desde el custom element) para que
    // event.target tenga name/value/className y el handler del tema lo trate
    // igual que a cualquier otro filtro, incluida la rama de mobile.
    const source = this.checkboxes[hi] || this.checkboxes[0];
    if (!source) return;

    source.dispatchEvent(new Event('input', { bubbles: true }));
    source.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('rugit-range-filter')) {
  customElements.define('rugit-range-filter', RugitRangeFilter);
}

/**
 * Rugit — slider de precio.
 *
 * Reemplaza el price-range del tema, que en ARS calcula mal el `max`
 * de los inputs y los deja topeados en 100.
 *
 * Los <input type="range"> son la UI; la verdad son dos inputs
 * numéricos ocultos con los name reales (filter.v.price.gte / lte).
 * Cuando un extremo está en su tope, el input se deshabilita para que
 * no viaje en la URL y el filtro quede limpio.
 */
class RugitPriceFilter extends HTMLElement {
  connectedCallback() {
    this.max = parseInt(this.dataset.max, 10);
    this.handles = Array.from(this.querySelectorAll('input[type="range"]'));
    this.gte = this.querySelector('[data-role="gte"]');
    this.lte = this.querySelector('[data-role="lte"]');
    this.track = this.querySelector('.rugit-range__track');
    this.readout = this.querySelector('[data-readout]');

    if (this.handles.length !== 2 || !this.track) return;

    try {
      this.fmt = new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: this.dataset.currency || 'ARS',
        maximumFractionDigits: 0
      });
    } catch (e) {
      this.fmt = { format: (n) => String(n) };
    }

    this.handles.forEach((h) => {
      h.addEventListener('input', this.paint.bind(this));
      h.addEventListener('change', this.commit.bind(this));
    });

    this.paint();
  }

  get bounds() {
    const a = parseInt(this.handles[0].value, 10);
    const b = parseInt(this.handles[1].value, 10);
    return [Math.min(a, b), Math.max(a, b)];
  }

  paint() {
    const [lo, hi] = this.bounds;
    this.track.style.setProperty('--range-min', (lo / this.max) * 100 + '%');
    this.track.style.setProperty('--range-max', (hi / this.max) * 100 + '%');
    if (this.readout) {
      this.readout.textContent = this.fmt.format(lo) + ' – ' + this.fmt.format(hi);
    }

    if (typeof rugitSyncSummaries === 'function') rugitSyncSummaries();
  }

  commit() {
    const [lo, hi] = this.bounds;

    // Extremo en su tope = sin filtro por ese lado. Input deshabilitado
    // no entra en el FormData, así que la URL queda limpia.
    this.gte.disabled = lo <= 0;
    this.gte.value = lo;
    this.lte.disabled = hi >= this.max;
    this.lte.value = hi;

    const source = this.gte.disabled ? this.lte : this.gte;
    source.dispatchEvent(new Event('input', { bubbles: true }));
    source.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('rugit-price-filter')) {
  customElements.define('rugit-price-filter', RugitPriceFilter);
}

/**
 * Rugit — resumen de selección y contador de la cabecera.
 *
 * No se puede depender del HTML que manda el servidor: al filtrar, el
 * JS del tema reemplaza todos los .js-filter MENOS el que disparó el
 * cambio (para no cerrarle el acordeón al usuario), así que ese queda
 * con su resumen viejo. Y la cabecera está fuera de los .js-filter,
 * así que no se actualiza nunca.
 *
 * Por eso todo se calcula acá, leyendo qué inputs están marcados.
 */
function rugitSyncSummaries() {
  let total = 0;

  document.querySelectorAll('.rugit-facets-on details.js-filter').forEach((details) => {
    const enDesktop = !!details.closest('#CollectionFiltersForm');
    const esOrden = details.classList.contains('rugit-sort');
    const readouts = details.querySelectorAll('.rugit-range__readout');
    let text = '';
    let count = 0;

    if (readouts.length) {
      details.querySelectorAll('rugit-range-filter').forEach((el) => {
        const marcados = Array.from(el.querySelectorAll('input[type="checkbox"]'));
        if (marcados.some((cb) => cb.checked)) count++;
      });

      details.querySelectorAll('rugit-price-filter').forEach((el) => {
        const gte = el.querySelector('[data-role="gte"]');
        const lte = el.querySelector('[data-role="lte"]');
        if ((gte && !gte.disabled) || (lte && !lte.disabled)) count++;
      });

      // Un slider siempre tiene posición, así que el readout nunca está
      // vacío. Solo mostramos el resumen si el usuario efectivamente
      // movió algo; si no, el grupo cerrado no dice nada.
      if (count > 0) {
        text = Array.from(readouts)
          .map((r) => r.textContent.replace(/\s+/g, ' ').trim())
          .filter(Boolean)
          .join('  |  ');
      }
    } else {
      // Listas: tomamos la etiqueta visible, que ya viene traducida
      // (SALE y no badge-sale).
      const etiquetas = Array.from(details.querySelectorAll('.rugit-facets__list input:checked'))
        .map((input) => {
          const label = input.closest('label');
          const span = label && (label.querySelector('.rugit-facets__value') || label.querySelector('.rugit-swatch__label'));
          return span ? span.textContent.trim() : '';
        })
        .filter(Boolean);

      text = etiquetas.join(' \u2013 ');
      count = etiquetas.length;

      // El orden siempre tiene una opción marcada. Solo lo mostramos
      // si difiere del orden por defecto de la colección.
      if (esOrden) {
        const elegido = details.querySelector('input[name="sort_by"]:checked');
        const porDefecto = details.dataset.defaultSort || '';
        if (!elegido || elegido.value === porDefecto) text = '';
      }
    }

    // El orden no es un filtro: no suma al contador.
    if (enDesktop && !esOrden) total += count;

    const summary = details.querySelector('summary');
    if (!summary) return;
    let target = summary.querySelector('.rugit-facet-summary');

    if (!text) {
      if (target) target.remove();
      return;
    }
    if (!target) {
      target = document.createElement('span');
      target.className = 'rugit-facet-summary';
      summary.appendChild(target);
    }
    // Solo escribimos si cambió: si no, el MutationObserver de abajo
    // se dispararía con nuestros propios cambios en loop.
    if (target.textContent !== text) target.textContent = text;
  });

  const titulo = document.querySelector('.rugit-facets__title');
  if (titulo) {
    // El número va en su propio span para poder estilarlo aparte.
    // `total` es siempre un entero calculado acá, así que no hay
    // riesgo de inyección al usar innerHTML.
    const nuevo = total > 0
      ? 'Filtros <span class="rugit-facets__count">(' + total + ')</span>'
      : 'Filtros';
    if (titulo.innerHTML !== nuevo) titulo.innerHTML = nuevo;
  }
}

document.addEventListener('DOMContentLoaded', function () {
  rugitSyncSummaries();

  // El re-render del tema es asincrónico y no emite ningún evento
  // propio, así que observamos el DOM y resincronizamos.
  const contenedor = document.querySelector('.collection-filters');
  if (!contenedor) return;
  let timer;
  new MutationObserver(function () {
    clearTimeout(timer);
    timer = setTimeout(rugitSyncSummaries, 60);
  }).observe(contenedor, { childList: true, subtree: true });
});